import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useApp } from '../contexts/AppContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiUsers, FiMessageCircle, FiBookOpen, FiTarget, 
  FiStar, FiShoppingBag, FiCalendar, FiPlus,
  FiTrendingUp, FiAward, FiClock, FiMapPin,
  FiSearch, FiFilter, FiX
} = FiIcons;

const Community = () => {
  const { addNotification } = useApp();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Community statistics
  const communityStats = {
    totalMembers: 1247,
    activeGroups: 45,
    recentPosts: 89,
    onlineNow: 156
  };

  // Featured community sections
  const communitySections = [
    {
      id: 'study-groups',
      name: 'Study Groups',
      description: 'Join collaborative learning groups',
      icon: FiUsers,
      color: 'bg-blue-500',
      path: '/study-groups',
      count: 45,
      trending: true
    },
    {
      id: 'chat',
      name: 'Community Chat',
      description: 'Real-time discussions with peers',
      icon: FiMessageCircle,
      color: 'bg-green-500',
      path: '/chat',
      count: 89,
      trending: false
    },
    {
      id: 'marketplace',
      name: 'Marketplace',
      description: 'Buy and sell study materials',
      icon: FiShoppingBag,
      color: 'bg-purple-500',
      path: '/marketplace',
      count: 67,
      trending: true
    },
    {
      id: 'user-directory',
      name: 'User Directory',
      description: 'Connect with fellow learners',
      icon: FiUsers,
      color: 'bg-orange-500',
      path: '/users',
      count: 1247,
      trending: false
    }
  ];

  // Recent community activities
  const recentActivities = [
    {
      id: 1,
      type: 'post',
      user: 'Aminata Kargbo',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b77c?w=100&h=100&fit=crop&crop=face',
      action: 'shared a study resource',
      subject: 'Mathematics Study Guide',
      timestamp: '2 hours ago',
      likes: 12,
      comments: 3
    },
    {
      id: 2,
      type: 'achievement',
      user: 'Omar Jallow',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
      action: 'completed 50 quizzes',
      subject: 'Science Mastery Badge',
      timestamp: '4 hours ago',
      likes: 24,
      comments: 8
    },
    {
      id: 3,
      type: 'group',
      user: 'Fatou Conteh',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
      action: 'created a new study group',
      subject: 'English Literature Discussion',
      timestamp: '6 hours ago',
      likes: 18,
      comments: 5
    }
  ];

  const handleSectionClick = (section) => {
    navigate(section.path);
    addNotification({
      type: 'info',
      title: 'Navigating',
      message: `Opening ${section.name}...`
    });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="space-y-6 px-4 py-4 pb-16">
      {/* Header */}
      <motion.div initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}} className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
        <div className="text-center">
          <SafeIcon icon={FiUsers} className="w-16 h-16 mx-auto mb-4 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Community Hub</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Connect, collaborate, and learn together with fellow students across West Africa and beyond
          </p>
        </div>
      </motion.div>

      {/* Community Stats */}
      <motion.div initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}} transition={{delay: 0.1}} className="bg-gradient-to-r from-primary-500 to-accent-500 rounded-xl shadow-lg p-6 text-white">
        <div className="text-center mb-4">
          <h2 className="text-xl font-bold mb-2">Community at a Glance</h2>
          <p className="text-primary-100">Growing stronger together every day</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white bg-opacity-20 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold">{communityStats.totalMembers}</div>
            <div className="text-sm">Total Members</div>
          </div>
          <div className="bg-white bg-opacity-20 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold">{communityStats.activeGroups}</div>
            <div className="text-sm">Active Groups</div>
          </div>
          <div className="bg-white bg-opacity-20 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold">{communityStats.recentPosts}</div>
            <div className="text-sm">Recent Posts</div>
          </div>
          <div className="bg-white bg-opacity-20 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold">{communityStats.onlineNow}</div>
            <div className="text-sm">Online Now</div>
          </div>
        </div>
      </motion.div>

      {/* Search Bar */}
      <motion.div initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}} transition={{delay: 0.2}} className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
        <div className="relative">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search community, groups, or users..."
            className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          {searchTerm && (
            <button onClick={clearSearch} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          )}
        </div>
      </motion.div>

      {/* Community Sections */}
      <motion.div initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}} transition={{delay: 0.3}} className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Explore Community Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {communitySections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{opacity: 0, y: 20}}
              animate={{opacity: 1, y: 0}}
              transition={{delay: 0.4 + index * 0.1}}
              onClick={() => handleSectionClick(section)}
              className="relative group cursor-pointer bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-all hover:shadow-lg"
            >
              {section.trending && (
                <div className="absolute top-3 right-3 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                  Trending
                </div>
              )}
              <div className="flex items-start space-x-4">
                <div className={`${section.color} p-3 rounded-xl flex-shrink-0`}>
                  <SafeIcon icon={section.icon} className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-lg font-bold text-gray-800">{section.name}</h3>
                    <span className="text-sm text-gray-500">{section.count}</span>
                  </div>
                  <p className="text-gray-600 text-sm mb-2">{section.description}</p>
                  <div className="flex items-center text-sm text-primary-600 font-medium group-hover:text-primary-700">
                    <span>Explore</span>
                    <SafeIcon icon={FiIcons.FiArrowRight} className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Recent Activity */}
      <motion.div initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}} transition={{delay: 0.5}} className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Recent Activity</h2>
          <button
            onClick={() => addNotification({type: 'info', title: 'Live Feed', message: 'Live feed updates enabled'})}
            className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1"
          >
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span>Live</span>
          </button>
        </div>
        <div className="space-y-4">
          {recentActivities.map((activity) => (
            <div key={activity.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
              <img src={activity.avatar} alt={activity.user} className="w-10 h-10 rounded-full object-cover" />
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="font-medium text-gray-800">{activity.user}</span>
                  <span className="text-sm text-gray-500">{activity.timestamp}</span>
                </div>
                <p className="text-sm text-gray-700 mb-1">
                  <span className="text-gray-600">{activity.action}</span>
                  <span className="font-medium"> "{activity.subject}"</span>
                </p>
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <span className="flex items-center space-x-1">
                    <SafeIcon icon={FiHeart} className="w-3 h-3" />
                    <span>{activity.likes}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <SafeIcon icon={FiMessageCircle} className="w-3 h-3" />
                    <span>{activity.comments}</span>
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}} transition={{delay: 0.6}} className="bg-gradient-to-r from-primary-500 to-accent-500 rounded-xl shadow-lg p-6 text-white">
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold mb-2">Get Started</h2>
          <p className="text-primary-100">Join the conversation and start learning together</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/study-groups')}
            className="bg-white bg-opacity-20 backdrop-blur-sm border border-white border-opacity-30 rounded-lg p-4 hover:bg-opacity-30 transition-colors"
          >
            <div className="flex items-center justify-center space-x-2">
              <SafeIcon icon={FiPlus} className="w-5 h-5" />
              <span className="font-medium">Create Study Group</span>
            </div>
          </button>
          <button
            onClick={() => navigate('/chat')}
            className="bg-white bg-opacity-20 backdrop-blur-sm border border-white border-opacity-30 rounded-lg p-4 hover:bg-opacity-30 transition-colors"
          >
            <div className="flex items-center justify-center space-x-2">
              <SafeIcon icon={FiMessageCircle} className="w-5 h-5" />
              <span className="font-medium">Start Conversation</span>
            </div>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Community;